import OneSchemaImporter from '@oneschema/react';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import { listsStateJ } from '../../../../../../services/state/ListsStateJ';
import { DealModal } from '../../../../../../pages/DealFlow2/components/DealModal/DealModal';
import { uploadList } from '../../../../../../services/queries/MaggieListQueries';
import { getEnvVariable, isDevelopment, isLocal } from '../../../../../../util/environment';
import { useModalState } from '../../../../../Modal/ModalContext';
import { useToastMessageState } from '../../../../../ToastMessage/ToastMessageProvider';
import { IListDataModel } from '../../../../../../data-models/list.data-model';
import { getListUploadWebhookKey } from '../../../../../../util/oneschema';
import { CompanyListForm } from './CompanyListForm';
import { FormStep } from './FormStep';

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
`;

export const UploadCompanyListForm = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [jwt, setJwt] = useState('');
  const { onCloseModal } = useModalState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const oneSchemaClientId = getEnvVariable('VITE_ONESCHEMA_CLIENT_ID');
  const webhookKey = getListUploadWebhookKey();

  const handleSubmit = useAtomCallback(
    useCallback(async (get, set, values: Partial<IListDataModel>) => {
      const { token, list } = await uploadList(values as unknown as IListDataModel);
      const prev = (await get(listsStateJ)) ?? [];
      set(listsStateJ, [...prev, list]);
      setJwt(token);
      setActiveStep(2);
      setIsOpen(true);
    }, [])
  );

  const handleSuccessUpload = () => {
    pushSuccessToast({ message: `Company list uploaded successfully` });
    onCloseModal();
  };

  const handleErrorUpload = () => {
    pushErrorToast({ message: 'An error occurred while uploading the file' });
  };

  return (
    <DealModal title={'Upload New Companies List'} width={'814px'}>
      <Wrapper>
        <FormStep
          activeStep={activeStep}
          stepNumber={1}
          heading={'Settings'}
          description={'Set list name and source. Also, share the list with colleagues'}
        >
          {activeStep === 1 && <CompanyListForm isEdit={false} onSubmit={handleSubmit} />}
        </FormStep>
        <FormStep
          activeStep={activeStep}
          stepNumber={2}
          heading={'Upload'}
          description={'Select XLSX or CSV file'}
        >
          <OneSchemaImporter
            isOpen={isOpen}
            onRequestClose={() => setIsOpen(false)}
            clientId={oneSchemaClientId}
            userJwt={jwt}
            templateKey='company_lists_upload'
            importConfig={{ type: 'webhook', key: webhookKey }}
            devMode={isDevelopment() || isLocal()}
            className='oneschema-importer'
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              zIndex: 2147483647,
            }}
            onSuccess={handleSuccessUpload}
            onError={handleErrorUpload}
          />
        </FormStep>
      </Wrapper>
    </DealModal>
  );
};
